import React, { memo } from 'react';
import Deals from 'pages/admin/deals';
import Leads from 'pages/admin/leads';
import { Col, HasAccess, Row } from 'components';
import { FilterButton } from './FilterButton';
import { TasksEntity } from 'pages/admin/tasks';
import { PERMISSIONS } from 'constants/permissions';
import { PurchaseRequests } from 'pages/admin/purchaseRequest';
import { useOutletContext } from 'react-router-dom';
import { useAccessControl } from '../../../components/common/accessControl/useAccessControl';

interface DashboardContentProps {
  selectedEntity: string;
  entityTypes: Record<string, { label: string; permission?: string }>;
  entityCounts: Record<string, number>;
  onFilterClick: <T extends string>(entity: T) => void;
  onUpdateCount: (entity: string, count: number) => void;
}

export const DashboardContent = memo(() => {
  const { hasAccess } = useAccessControl();
  const { selectedEntity, entityTypes, entityCounts, onFilterClick, onUpdateCount } =
    useOutletContext<DashboardContentProps>();
  return (
    <Col span={24} className={'dashboard-content'}>
      <Row className="dashboard-filters" wrap={false}>
        {Object.entries(entityTypes)
          .filter(([_, i]) => {
            return !i.permission || hasAccess(i.permission);
          })
          .map(([type, value]) => (
            <FilterButton
              key={type}
              label={value.label}
              count={entityCounts[type]}
              isSelected={selectedEntity === type}
              onClick={() => onFilterClick(type)}
            />
          ))}
      </Row>

      {(selectedEntity === 'ALL' || selectedEntity === 'PURCHASE_REQUEST') && (
        <HasAccess role={PERMISSIONS.purchaseRequests.purchaseRequest.read}>
          <PurchaseRequests
            forDashboard={true}
            onTotalUpdate={(total: number) => onUpdateCount('PURCHASE_REQUEST', total)}
          />
        </HasAccess>
      )}
      {(selectedEntity === 'ALL' || selectedEntity === 'DEAL') && (
        <HasAccess role={PERMISSIONS.deals.deal.read}>
          <Deals forDashboard={true} onTotalUpdate={(total: number) => onUpdateCount('DEAL', total)} />
        </HasAccess>
      )}
      {(selectedEntity === 'ALL' || selectedEntity === 'LEAD') && (
        <HasAccess role={PERMISSIONS.leads.lead.read}>
          <Leads forDashboard={true} onTotalUpdate={(total: number) => onUpdateCount('LEAD', total)} />
        </HasAccess>
      )}
      {(selectedEntity === 'ALL' || selectedEntity === 'TASK') && (
        <HasAccess role={PERMISSIONS.tasks.task.read}>
          <TasksEntity forDashboard={true} onTotalUpdate={(total: number) => onUpdateCount('TASK', total)} />
        </HasAccess>
      )}
    </Col>
  );
});
